import { Vote, Copy, Workflow, MapPinned, MessagesSquare, ShieldCheck } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const capabilities = [
  {
    icon: Copy,
    title: 'Deterministic + probabilistic dedup',
    description:
      'Weighted attribute scoring (ssn_last4 0.40, dob 0.25, last_name 0.15, first_name 0.10, address 0.10) with deterministic override and classification audit.',
  },
  {
    icon: Workflow,
    title: 'Eligibility state machine',
    description:
      'Registrants move through an explicit state machine — pending, verified, challenged, inactive, revoked — with allowed-transition enforcement.',
  },
  {
    icon: MapPinned,
    title: 'Cross-jurisdiction routing',
    description:
      'Matches that span jurisdictions route to election officials on both sides with scoped visibility and a shared dispute channel.',
  },
  {
    icon: MessagesSquare,
    title: 'Dispute workflow',
    description:
      'Citizens and officials correspond through a guided, signed workflow. Every exchange is preserved on the integrity chain.',
  },
  {
    icon: ShieldCheck,
    title: 'Tamper-evident history',
    description:
      'Registration edits are hash-chained per registrant; historical states are fully reconstructible for audit or litigation.',
  },
  {
    icon: Vote,
    title: 'Election-official workflow',
    description:
      'Dashboard, dedup match review, eligibility state edits, and dispute handling — all in one role-scoped surface with real-time updates.',
  },
];

export default function VERISPage() {
  return (
    <>
      <Hero
        eyebrow="VERIS — Voter Eligibility & Registration Integrity"
        title="Integrity for the highest-stakes list."
        description="Dedup, eligibility, disputes, and cross-jurisdiction routing — the full workflow a modern election office needs, with audit that holds up in litigation."
        primaryCta={{ to: '/for-partners', label: 'Election official access' }}
        secondaryCta={{ to: '/contact', label: 'Contact us' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Capabilities"
            title="Dedup, eligibility, disputes — one integrated surface."
          />
          <div className="mt-12">
            <FeatureGrid items={capabilities} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="Dedup algorithm"
        title="Transparent, tunable, and verifiable."
        description="The VERIS dedup matcher is weighted attribute scoring — not a black-box model. Every match is published with the factors and weights that produced it; reviewers can trust, challenge, or override with full transparency."
        bullets={[
          'Attribute weights declared in configuration, not buried in model weights',
          'Deterministic match > probabilistic match — never the other way around',
          'Classification stored alongside the match record for audit',
        ]}
        image={<MockVisual accent="amber" lines={6} />}
      />

      <Slab
        reverse
        eyebrow="State machine"
        title="No registrant is in an undefined state."
        description="Every registrant sits in exactly one eligibility state, and transitions are enforced by an allowed-transition matrix. An official attempting an invalid transition gets a 409 — no silent data corruption, ever."
        bullets={[
          'States: pending → verified → challenged → inactive → revoked',
          'Transitions require matching role (election_official or veris_admin)',
          'Every transition is signed and chained per registrant',
        ]}
        image={<MockVisual accent="sky" lines={5} />}
      />

      <CTABanner
        title="Bring your jurisdiction onto VERIS."
        description="Tiered onboarding from observer to fully-federated election-office integration. All integrations run through the Interoperability Sandbox."
        primaryCta={{ to: '/contact', label: 'Talk to our team' }}
      />
    </>
  );
}
